import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, GraduationCap, CheckCircle2, Circle, Loader2, PlayCircle, Bot, ShieldCheck, Target } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import AIMentor from "@/components/AIMentor";
import { toast } from "sonner";
import api from "@/lib/api";
import aiLessonContent from "./aiLessonContent";

export default function CoursePlayer() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [active, setActive] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = () => api.get(`/me/course/${slug}`).then((r) => {
        setData(r.data);
        if (!active) {
            const first = r.data.modules.flatMap((m) => m.lessons).find((l) => !l.completed) || r.data.modules[0]?.lessons[0];
            setActive(first || null);
        }
    }).catch(() => setData({ modules: [], enrolled: false }));
    useEffect(() => { load(); }, [slug]);

    const complete = async (lessonId) => {
        setBusy(true);
        try { const { data: r } = await api.post(`/me/lessons/${lessonId}/complete`); toast.success(`Lesson complete — ${r.progress}% done`); await load(); }
        catch { toast.error("Could not save progress."); }
        finally { setBusy(false); }
    };

    const completeChallenge = async (id) => {
        setBusy(true);
        try { await api.post(`/me/challenges/${id}/complete`); toast.success("Challenge completed! 🎯"); await load(); setActive((a) => (a ? { ...a, completed: true } : a)); }
        catch { toast.error("Could not save."); }
        finally { setBusy(false); }
    };

    if (!data) return <div className="min-h-screen grid place-items-center bg-background"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

    if (!data.enrolled)
        return (
            <div className="min-h-screen grid place-items-center bg-background p-6 text-center">
                <div>
                    <GraduationCap className="h-10 w-10 text-primary mx-auto" />
                    <h1 className="mt-4 font-heading text-2xl font-bold">You're not enrolled yet</h1>
                    <p className="mt-2 text-foreground/60">Enroll in this program to access the course player.</p>
                    <div className="mt-6 flex gap-2 justify-center">
                        <Button asChild variant="outline" className="rounded-full"><Link to="/dashboard">Dashboard</Link></Button>
                        <Button asChild className="rounded-full"><Link to={`/programs/${slug}`}>View Program</Link></Button>
                    </div>
                </div>
            </div>
        );

    return (
        <div className="min-h-screen bg-muted/30">
            <header className="sticky top-0 z-20 flex items-center gap-3 h-16 px-4 sm:px-8 bg-background/85 backdrop-blur-xl border-b border-border">
                <Link to="/dashboard" className="inline-flex items-center text-sm text-foreground/60 hover:text-primary"><ArrowLeft className="mr-1 h-4 w-4" /> Dashboard</Link>
                <h1 className="font-heading text-lg font-bold truncate ml-2">{data.program.title}</h1>
                <div className="ml-auto flex items-center gap-3 w-40"><Progress value={data.progress} className="h-2" /><span className="text-sm font-medium">{data.progress}%</span></div>
            </header>
            <div className="grid lg:grid-cols-12 gap-6 p-4 sm:p-8">
                <aside className="lg:col-span-4 xl:col-span-3">
                    <Accordion type="multiple" defaultValue={data.modules.map((m) => m.id)} className="space-y-2">
                        {data.modules.map((m) => (
                            <AccordionItem key={m.id} value={m.id} className="rounded-xl border border-border bg-card px-3">
                                <AccordionTrigger className="font-heading text-sm">{m.title}</AccordionTrigger>
                                <AccordionContent className="space-y-1 pb-2">
                                    {m.lessons.map((l) => (
                                        <button key={l.id} data-testid={`lesson-item-${l.id}`} onClick={() => setActive(l)}
                                            className={`w-full flex items-center gap-2 rounded-lg px-2.5 py-2 text-sm text-left transition-colors ${active?.id === l.id ? "bg-primary/10 text-primary" : "hover:bg-muted"}`}>
                                            {l.completed ? <CheckCircle2 className="h-4 w-4 text-success shrink-0" /> : <Circle className="h-4 w-4 text-foreground/30 shrink-0" />}
                                            <span className="truncate">{l.title}</span>
                                        </button>
                                    ))}
                                    {(m.challenges || []).map((c) => (
                                        <button key={c.id} data-testid={`challenge-item-${c.id}`} onClick={() => setActive({ ...c, _challenge: true })}
                                            className={`w-full flex items-center gap-2 rounded-lg px-2.5 py-2 text-sm text-left transition-colors ${active?.id === c.id ? "bg-accent/15" : "hover:bg-muted"}`}>
                                            {c.completed ? <CheckCircle2 className="h-4 w-4 text-success shrink-0" /> : <Target className="h-4 w-4 text-accent shrink-0" />}
                                            <span className="truncate">Challenge: {c.title}</span>
                                        </button>
                                    ))}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </aside>
                <main className="lg:col-span-8 xl:col-span-9">
                    {active && active._challenge ? (
                        <Card className="p-6 sm:p-8" data-testid="challenge-content">
                            <div className="flex items-center gap-2 flex-wrap">
                                <Badge className="rounded-full bg-accent/15 text-accent-foreground border-0"><Target className="mr-1 h-3.5 w-3.5" /> Challenge · Difficulty {active.difficulty}/5</Badge>
                                <Badge variant="outline" className="rounded-full">{active.estimated_time}</Badge>
                                <Badge variant="outline" className="rounded-full">AI mode: {active.ai_mode}</Badge>
                                <Badge variant="outline" className="rounded-full">{active.individual_or_team}</Badge>
                                {active.completed && <Badge className="rounded-full bg-success/15 text-success border-0"><CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Completed</Badge>}
                            </div>
                            <h2 className="mt-4 font-heading text-2xl font-bold">{active.title}</h2>
                            <p className="mt-3 text-foreground/70 leading-relaxed">{active.description}</p>
                            <div className="mt-5 rounded-xl bg-muted/50 p-4 text-sm text-foreground/70"><span className="font-heading font-semibold text-foreground">Deliverable:</span> {active.deliverables}</div>
                            <div className="mt-5 rounded-xl border border-primary/20 bg-primary/5 p-4 flex items-center gap-3 text-sm"><Bot className="h-5 w-5 text-primary" /> Practise here before your project and assessment. The AI Mentor can give hints — not answers.</div>
                            <Button disabled={active.completed || busy} data-testid="complete-challenge-btn" className="mt-6 rounded-full" onClick={() => completeChallenge(active.id)}>
                                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : active.completed ? <><CheckCircle2 className="mr-1.5 h-4 w-4" /> Completed</> : "Mark challenge complete"}
                            </Button>
                        </Card>
                    ) : active ? (
                        <Card className="p-6 sm:p-8" data-testid="lesson-content">
                            <div className="flex items-center gap-2 flex-wrap">
                                <Badge variant="secondary" className="rounded-full">AI mode: {active.ai_mode}</Badge>
                                <Badge variant="outline" className="rounded-full"><PlayCircle className="mr-1 h-3.5 w-3.5" /> {active.duration}</Badge>
                                {active.completed && <Badge className="rounded-full bg-success/15 text-success border-0"><CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Completed</Badge>}
                            </div>
                            <h2 className="mt-4 font-heading text-2xl font-bold">{active.title}</h2>
                            <p className="mt-3 text-foreground/70 leading-relaxed"><span className="font-semibold text-foreground">Learning objective:</span> {active.objective}</p>
                                {(() => {

    const lesson = aiLessonContent[active.title];

    if (!lesson) {
        return (
            <div className="mt-6 rounded-xl bg-muted/50 p-5 text-sm text-foreground/70">
                <p className="font-semibold text-foreground">Lesson activity</p>
                <p className="mt-2">Review the learning objective, practise the skill, and apply it to a real-world example.</p>
            </div>
        );
    }

    return (
        <div className="mt-6 space-y-5">
            <div className="rounded-xl bg-muted/50 p-5">
                <h3 className="font-heading font-semibold text-foreground">Lesson introduction</h3>
                <p className="mt-2 text-sm text-foreground/70 leading-relaxed">{lesson.introduction}</p>
            </div>

            {lesson.sections.map((section) => (
                <div key={section.title} className="rounded-xl border border-border bg-card p-5">
                    <h3 className="font-heading font-semibold text-foreground">{section.title}</h3>
                    <p className="mt-2 text-sm text-foreground/70 leading-relaxed">{section.content}</p>
                </div>
            ))}

            <div className="rounded-xl border border-primary/20 bg-primary/5 p-5">
                <h3 className="font-heading font-semibold text-foreground">Reflection question</h3>
                <p className="mt-2 text-sm text-foreground/70 leading-relaxed">{lesson.reflection}</p>
            </div>
        </div>
    );
})()}
                            
                            <div className="mt-6 rounded-xl border border-primary/20 bg-primary/5 p-4 flex items-center gap-3 text-sm"><Bot className="h-5 w-5 text-primary" /> Stuck? Ask the AI Mentor for a hint — it won't do the work for you.</div>
                            <div className="mt-6 flex items-center gap-3">
                                <Button disabled={active.completed || busy} data-testid="mark-complete-btn" className="rounded-full" onClick={() => complete(active.id)}>
                                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : active.completed ? <><CheckCircle2 className="mr-1.5 h-4 w-4" /> Completed</> : "Mark lesson complete"}
                                </Button>
                                {data.progress === 100 && <span className="flex items-center gap-1.5 text-sm text-success"><ShieldCheck className="h-4 w-4" /> Course complete — take the assessment!</span>}
                            </div>
                        </Card>
                    ) : <Card className="p-8 text-center text-foreground/50">Select a lesson to begin.</Card>}
                </main>
            </div>
            <AIMentor />
        </div>
    );
}
